import React, { useState } from 'react';
import DatasetDialog, { Field, inputClass, primaryClass, neutralClass } from 'features/datasets/components/DatasetDialog';
import { exportDataset } from 'features/datasets/api/datasetsApi';

export const ROW_OPTIONS = [
  { value: 'all', label: 'Every row' },
  { value: 'expected', label: 'Rows with an Expected' },
  { value: 'no_expected', label: 'Rows without an Expected' },
  { value: 'passed', label: 'Rows that passed their evals' },
  { value: 'failed', label: 'Rows that failed an eval' },
  { value: 'marked_wrong', label: 'Rows with a verdict marked wrong' },
];

export const FORMAT_OPTIONS = [
  { value: 'trodo', label: 'Trodo rows (JSONL)' },
  { value: 'openai', label: 'Chat fine-tuning (OpenAI, Fireworks, Together)' },
  { value: 'dpo', label: 'Preference pairs (DPO)' },
];

export const TARGET_OPTIONS = [
  { value: 'expected_else_output', label: 'Expected, else Output' },
  { value: 'output', label: 'Output (what the model said)' },
  { value: 'expected', label: 'Expected only' },
];

/** Save `text` as a file in the browser. */
export function downloadText(text, filename, type = 'application/x-ndjson') {
  const blob = new Blob([text], { type });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * Export a dataset as JSONL: which rows (by Expected or by their eval results),
 * as Trodo rows or as fine-tuning examples, the live rows or a saved version,
 * or just the ticked rows (docs/DATASETS.md).
 */
export default function ExportDialog({ dataset, versions = [], itemIds = null, onClose }) {
  const [rows, setRows] = useState('all');
  const [format, setFormat] = useState('trodo');
  const [target, setTarget] = useState('expected_else_output');
  const [version, setVersion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const selection = Array.isArray(itemIds) && itemIds.length ? itemIds : null;

  const run = async () => {
    setBusy(true); setError(null);
    try {
      const { text, rows: n, skipped } = await exportDataset(dataset.id, { rows, format, target, version: !selection && version ? Number(version) : null, ids: selection });
      const safe = String(dataset.name || 'dataset').replace(/[^\w.-]+/g, '_');
      downloadText(text, `${safe}${version && !selection ? `.v${version}` : ''}${selection ? '.selection' : ''}${format !== 'trodo' ? `.${format}` : ''}.jsonl`);
      setDone({ n, skipped });
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const footer = (
    <>
      <span className="mr-auto text-[11px] text-[color:var(--text-muted)]" aria-live="polite">
        {done != null ? `${done.n.toLocaleString()} ${format === 'trodo' ? 'rows' : 'examples'} downloaded${done.skipped ? `, ${done.skipped.toLocaleString()} skipped (no input or no target)` : ''}` : ''}
      </span>
      <button type="button" onClick={onClose} className={neutralClass}>{done != null ? 'Close' : 'Cancel'}</button>
      <button type="button" onClick={run} disabled={busy} className={primaryClass}>{busy ? 'Exporting…' : 'Download'}</button>
    </>
  );

  return (
    <DatasetDialog title={selection ? `Export ${selection.length} selected rows` : `Export · ${dataset.name}`} onClose={onClose} width="max-w-[520px]" footer={footer}>
      <Field label="Rows" htmlFor="dx-rows">
        <select id="dx-rows" className={`${inputClass} appearance-none`} value={rows} onChange={(e) => setRows(e.target.value)}>
          {ROW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="As" htmlFor="dx-format">
        <select id="dx-format" className={`${inputClass} appearance-none`} value={format} onChange={(e) => setFormat(e.target.value)}>
          {FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      {format === 'openai' ? (
        <Field label="Target" htmlFor="dx-target">
          <select id="dx-target" className={`${inputClass} appearance-none`} value={target} onChange={(e) => setTarget(e.target.value)}>
            {TARGET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      ) : null}
      {!selection ? (
        <Field label="Version" htmlFor="dx-version">
          <select id="dx-version" className={`${inputClass} appearance-none`} value={version} onChange={(e) => setVersion(e.target.value)}>
            <option value="">Live rows</option>
            {versions.map((v) => <option key={v.version_no} value={v.version_no}>v{v.version_no}{v.name && v.name !== `v${v.version_no}` ? ` · ${v.name}` : ''} · {v.item_count} rows</option>)}
          </select>
        </Field>
      ) : null}
      {error ? <p className="text-[12px] text-danger">{error}</p> : null}
    </DatasetDialog>
  );
}
